import { makeAssistantToolUI } from '@assistant-ui/react';
import { useGqlQueryVariables } from '../../../hooks/use-gql-query-variables';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Card, cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shadcn/ui';
import { Check, ChevronDown, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../../../../providers/authentication-provider';

function ActionsListDisplay({
    gqlQueryVariables,
    userRequest,
    error,
    status,
    toolCallId,
}: {
    userRequest: string;
    error: string;
    gqlQueryVariables: any;
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
    toolCallId: string;
}) {
    const {
        authState: { isGlobalAdmin },
    } = useAuth();
    const { setGqlQueryVariables, setActiveFilterId, activeFilterId, setUserRequest, setError } =
        useGqlQueryVariables();
    const previousStatusRef = useRef<'running' | 'complete' | 'incomplete' | 'requires-action' | undefined>(status);

    const handleRemoveFilter = useCallback(() => {
        setGqlQueryVariables(undefined);
        setActiveFilterId(undefined);
        setUserRequest(undefined);
        setError(undefined);
    }, [setGqlQueryVariables, setActiveFilterId, setUserRequest, setError]);

    const handleApplyFilter = useCallback(() => {
        setGqlQueryVariables(gqlQueryVariables);
        setActiveFilterId(toolCallId);
        setUserRequest(userRequest || 'These actions are based on your filter');
        if (error) {
            setError(error);
        }
    }, [gqlQueryVariables, setGqlQueryVariables, setActiveFilterId, toolCallId, setUserRequest, error]);

    useEffect(() => {
        const previousStatus = previousStatusRef.current;

        if (status === 'complete' && previousStatus !== 'complete') {
            handleApplyFilter();
        }

        // Update the previous status ref for the next render
        previousStatusRef.current = status;
    }, [status, handleApplyFilter]);

    useEffect(() => {
        if (status === 'running') {
            setActiveFilterId(toolCallId);
        }
    }, [status, toolCallId, setActiveFilterId]);

    return (
        <Card
            className={cn(
                'mx-auto my-2 w-full max-w-md flex flex-col gap-2 p-4 bg-gradient-to-b from-background-brand-secondary-section/50 to-background-primary'
            )}
        >
            <div className="flex flex-row gap-2 items-center">
                <Sparkles className="w-4 h-4 text-brand-secondary" />
                <span className="font-medium">Custom filter</span>
            </div>
            {status === 'running' && (
                <>
                    <p className="text-sm text-muted-foreground animate-pulse">Generating actions...</p>
                    <p className="text-sm text-muted-foreground animate-pulse">{userRequest}</p>
                </>
            )}
            {status === 'complete' && (
                <>
                    <p className="text-sm text-muted-foreground">
                        {userRequest || 'These actions are based on your filter'}
                    </p>

                    <div className="relative">
                        {(isGlobalAdmin || import.meta.env.DEV) && (
                            <Collapsible>
                                <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="flex gap-1 items-center text-xs">
                                        <span>View filter details (only visible to admins)</span>
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <pre className="whitespace-pre-wrap break-all text-left text-xs mt-2 p-2 bg-muted rounded-md">
                                        {JSON.stringify(gqlQueryVariables, null, 4)}
                                    </pre>
                                </CollapsibleContent>
                            </Collapsible>
                        )}
                    </div>
                    <div className="flex flex-row gap-2 w-full justify-end">
                        {activeFilterId !== toolCallId && (
                            <Button variant="outline" className="flex gap-2" onClick={handleApplyFilter}>
                                <Check className="w-4 h-4" />
                                Apply filter
                            </Button>
                        )}
                        {activeFilterId === toolCallId && (
                            <Button variant="outline" className="flex gap-2" onClick={handleRemoveFilter}>
                                <X className="w-4 h-4" />
                                Remove filter
                            </Button>
                        )}
                    </div>
                </>
            )}
        </Card>
    );
}

// export const ActionsListTool = makeAssistantToolUI<ActionsListToolArgs, string>({
//     toolName: 'actions_list',
//     render: function ActionsListUI({ args, result }) {
//         let resultObj: ActionsListToolResult | { error: string };
//         try {
//             resultObj = result ? JSON.parse(result) : { actions: [] };
//         } catch (e) {
//             resultObj = { error: result || 'Unknown error' };
//         }

//         return (
//             <div className="mb-4 flex flex-col items-center gap-2">
//                 <pre className="whitespace-pre-wrap break-all text-center">actions-list({JSON.stringify(args)})</pre>
//                 {'actions' in resultObj && <ActionsListDisplay actions={resultObj.actions} userId={args.userId} />}
//                 {'error' in resultObj && <p className="text-red-500">{resultObj.error}</p>}
//             </div>
//         );
//     },
// });

export const DisplayActionsToolUI = makeAssistantToolUI<
    {},
    { gqlQueryVariables: any; userRequest: string; error: string }
>({
    toolName: 'displayActionsTool',
    render: function ActionsListUI({ args, result, status, toolCallId }) {
        console.log('LLM result', result);
        console.log('LLM args', args);

        return (
            <div className="mb-4 flex flex-col items-center gap-2">
                <ActionsListDisplay {...result} status={status?.type} toolCallId={toolCallId} />
            </div>
        );
    },
});
